/**
 * @typedef {Object} LegendItem
 * @description Унифицированная структура элемента легенды
 * @property {string} id - Уникальный идентификатор элемента
 * @property {MessageDescriptor} label - Локализованный текст
 * @property {string} [color] - Цвет для визуализации (hex или named color)
 * @property {string} [fill] - Альтернативное имя для цвета (для совместимости)
 * @property {*} [value] - Значение для фильтров или идентификации
 * @property {string} [description] - Дополнительное описание
 * @property {Object} [metadata] - Специфичные данные для конкретной легенды
 */

/**
 * @typedef {Object} LegendConfig
 * @description Конфигурация легенды (тип, стиль отображения, данные)
 * @property {string} id - Уникальный ID легенды (напр. 'poi', 'population', 'aspect')
 * @property {string} type - Тип легенды: 'colored-items', 'gradient', 'terrain', 'custom', 'size-gradient', 'size-range-horizontal'
 * @property {MessageDescriptor} title - Заголовок легенды
 * @property {LegendItem[]} items - Массив элементов легенды
 * @property {string} [icon] - Тип иконки: 'circle', 'hexagon', 'rect', 'custom', 'none'
 * @property {Object} [renderConfig] - Конфигурация для рендеринга (css классы, стили и т.д.)
 * @property {boolean} [showIcon] - Показывать ли иконку (default: true для typed, false для custom)
 * @property {boolean} [showDescription] - Показывать ли описание элемента
 * @property {*} [customRenderer] - Функция/компонент для кастомного рендеринга (для типа 'custom')
 * @property {Object} [metadata] - Дополнительные метаданные легенды
 */

export const LEGEND_TYPES = {
    /** Список элементов с цветами (POI, New Buildings, Parking Statuses) */
    COLORED_ITEMS: 'colored-items',

    /** Градиент или тепловая карта */
    GRADIENT: 'gradient',

    /** Градуированный размер - концентрические круги разного размера */
    SIZE_GRADIENT: 'size-gradient',

    /** Распределённые размеры - круги разного размера, распределённые горизонтально */
    SIZE_RANGE_HORIZONTAL: 'size-range-horizontal',

    /** Специализированные terrain легенды (Slope, Aspect, Composite) */
    TERRAIN: 'terrain',

    /** Полностью кастомная легенда с собственным renderer'ом */
    CUSTOM: 'custom',

    /** Динамическая легенда для H3 слоёв (автоматическое масштабирование значений) */
    H3_DYNAMIC: 'h3-dynamic',
};

export const LEGEND_ICON_TYPES = {
    CIRCLE: 'circle',      // Для круглых элементов (POI, points)
    RING: 'ring',          // Кольцо (незалитый круг) — для точечных источников без заливки
    HEXAGON: 'hexagon',    // Для шестиугольников (aggregated data)
    RECT: 'rect',          // Для прямоугольников (landuse, zones)
    LINE: 'line',          // Для линий (routes, paths)
    CUSTOM: 'custom',      // Кастомная иконка
    NONE: 'none',          // Без иконки
};

export const LEGEND_RENDER_CONFIG_DEFAULTS = {
    [LEGEND_TYPES.COLORED_ITEMS]: {
        containerClass: 'innerLegendGrid',
        showIcon: true,
        iconSize: '24px',
        iconOpacity: 0.8,
    },
    [LEGEND_TYPES.GRADIENT]: {
        showIcon: false,
    },
    [LEGEND_TYPES.TERRAIN]: {
        containerClass: 'terrainLegendContainer',
        showIcon: false,
    },
    [LEGEND_TYPES.CUSTOM]: {
        showIcon: false,
    },
    [LEGEND_TYPES.H3_DYNAMIC]: {
        showIcon: false,
    },
};
